import { useRef, useLayoutEffect, useEffect } from 'react';
import gsap from 'gsap';
import * as THREE from 'three';

interface HeroProps {
  isDarkMode: boolean;
}

// --- TUNNEL CONFIG ---
const TUNNEL_RADIUS = 16;
const SEGMENT_DEPTH = 6;
const NUM_SEGMENTS = 22;
const RING_SECTORS = 16;
const PANEL_GRID_ANGULAR = 12;
const PANEL_GRID_DEPTH = 14;
const PIXEL_FILL_CHANCE = 0.78;
const PANEL_SPAWN_CHANCE = 0.55;

const MAX_TRAVEL = SEGMENT_DEPTH * NUM_SEGMENTS * 1.2;
const MAX_RECYCLES_PER_FRAME = 2;

let pixelTextureCache: THREE.Texture | null = null;
const getPixelTexture = (): THREE.Texture => {
  if (pixelTextureCache) return pixelTextureCache;
  const size = 16;
  const c = document.createElement('canvas');
  c.width = size;
  c.height = size;
  const ctx = c.getContext('2d');
  if (ctx) {
    ctx.fillStyle = 'rgba(255,255,255,1)';
    ctx.fillRect(1, 1, size - 2, size - 2);
  }
  const tex = new THREE.CanvasTexture(c);
  tex.magFilter = THREE.NearestFilter;
  tex.minFilter = THREE.NearestFilter;
  pixelTextureCache = tex;
  return tex;
};

const randomShade = (isDarkMode: boolean): number => {
  if (isDarkMode) {
    const r = Math.random();
    if (r < 0.12) return 0.95 + Math.random() * 0.05;
    if (r < 0.35) return 0.7 + Math.random() * 0.2;
    return 0.4 + Math.random() * 0.3;
  }
  const r = Math.random();
  if (r < 0.12) return 0.0 + Math.random() * 0.05;
  if (r < 0.35) return 0.1 + Math.random() * 0.2;
  return 0.35 + Math.random() * 0.3;
};

const createMosaicPanel = (
  radius: number,
  sectorIndex: number,
  sectorCount: number,
  depth: number,
  isDarkMode: boolean,
) => {
  const arcAngle = (Math.PI * 2) / sectorCount;
  const startAngle = sectorIndex * arcAngle;
  const angleMargin = arcAngle * 0.04;
  const depthMargin = depth * 0.04;

  const a0 = startAngle + angleMargin;
  const a1 = startAngle + arcAngle - angleMargin;
  const z0 = depthMargin;
  const z1 = depth - depthMargin;

  const cells: number[] = [];
  const cellColors: number[] = [];

  for (let ix = 0; ix < PANEL_GRID_ANGULAR; ix++) {
    for (let iz = 0; iz < PANEL_GRID_DEPTH; iz++) {
      if (Math.random() > PIXEL_FILL_CHANCE) continue;
      const tA = (ix + 0.5) / PANEL_GRID_ANGULAR;
      const tZ = (iz + 0.5) / PANEL_GRID_DEPTH;
      const angle = a0 + (a1 - a0) * tA;
      const z = -(z0 + (z1 - z0) * tZ);
      cells.push(Math.cos(angle) * radius, Math.sin(angle) * radius, z);

      const shade = randomShade(isDarkMode);
      cellColors.push(shade, shade, shade);
    }
  }

  const positions = new Float32Array(cells);
  const colors = new Float32Array(cellColors);

  const geom = new THREE.BufferGeometry();
  geom.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geom.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));

  const mat = new THREE.PointsMaterial({
    size: 0.55,
    map: getPixelTexture(),
    vertexColors: true,
    transparent: true,
    opacity: 0,
    sizeAttenuation: true,
    depthWrite: false,
    alphaTest: 0.5,
  });

  const points = new THREE.Points(geom, mat);
  points.name = 'mosaic_panel';
  gsap.to(mat, { opacity: 1, duration: 0.8, ease: 'power2.out' });
  return points;
};

const createRingWireframe = (radius: number, depth: number) => {
  const positions: number[] = [];
  const segments = 64;

  for (let ring = 0; ring < 2; ring++) {
    const z = ring === 0 ? 0 : -depth;
    for (let i = 0; i < segments; i++) {
      const a0 = (i / segments) * Math.PI * 2;
      const a1 = ((i + 1) / segments) * Math.PI * 2;
      positions.push(Math.cos(a0) * radius, Math.sin(a0) * radius, z);
      positions.push(Math.cos(a1) * radius, Math.sin(a1) * radius, z);
    }
  }

  for (let i = 0; i < RING_SECTORS; i++) {
    const angle = (i / RING_SECTORS) * Math.PI * 2;
    const x = Math.cos(angle) * radius;
    const y = Math.sin(angle) * radius;
    positions.push(x, y, 0);
    positions.push(x, y, -depth);
  }

  const geom = new THREE.BufferGeometry();
  geom.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  const mat = new THREE.LineBasicMaterial({
    color: 0xb0b0b0,
    transparent: true,
    opacity: 0.45,
  });
  return new THREE.LineSegments(geom, mat);
};

const populatePanels = (group: THREE.Group, isDarkMode: boolean) => {
  for (let i = 0; i < RING_SECTORS; i++) {
    if (Math.random() < PANEL_SPAWN_CHANCE) {
      const panel = createMosaicPanel(
        TUNNEL_RADIUS - 0.05,
        i,
        RING_SECTORS,
        SEGMENT_DEPTH,
        isDarkMode,
      );
      group.add(panel);
    }
  }
};

const recolorPanels = (group: THREE.Group, isDarkMode: boolean) => {
  group.children.forEach((child) => {
    if (child instanceof THREE.Points && child.name === 'mosaic_panel') {
      const colorAttr = child.geometry.getAttribute('color') as THREE.BufferAttribute;
      const count = colorAttr.count;
      for (let i = 0; i < count; i++) {
        const shade = randomShade(isDarkMode);
        colorAttr.setXYZ(i, shade, shade, shade);
      }
      colorAttr.needsUpdate = true;
    }
  });
};

const createSegment = (zPos: number, isDarkMode: boolean) => {
  const group = new THREE.Group();
  group.position.z = zPos;
  group.add(createRingWireframe(TUNNEL_RADIUS, SEGMENT_DEPTH));
  populatePanels(group, isDarkMode);
  return group;
};

const Hero = ({ isDarkMode }: HeroProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const hintRef = useRef<HTMLDivElement>(null);

  const sceneRef = useRef<THREE.Scene | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const segmentsRef = useRef<THREE.Group[]>([]);
  const isDarkModeRef = useRef(isDarkMode);

  const targetZRef = useRef(0);
  const currentZRef = useRef(0);
  const scrollProgressRef = useRef(0);
  const flickerAccumRef = useRef(0);

  useEffect(() => {
    isDarkModeRef.current = isDarkMode;
  }, [isDarkMode]);

  useEffect(() => {
    if (!canvasRef.current || !containerRef.current) return;

    const scene = new THREE.Scene();
    scene.fog = new THREE.Fog(0x0a0a0a, SEGMENT_DEPTH * 4, SEGMENT_DEPTH * NUM_SEGMENTS * 0.75);
    sceneRef.current = scene;

    const width = window.innerWidth;
    const height = window.innerHeight;
    const camera = new THREE.PerspectiveCamera(78, width / height, 0.1, 1000);
    camera.position.set(0, 0, 0);
    cameraRef.current = camera;

    const renderer = new THREE.WebGLRenderer({
      canvas: canvasRef.current,
      antialias: true,
      alpha: false,
      powerPreference: 'high-performance',
    });
    renderer.setSize(width, height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    rendererRef.current = renderer;

    const segments: THREE.Group[] = [];
    for (let i = 0; i < NUM_SEGMENTS; i++) {
      const z = -i * SEGMENT_DEPTH;
      const segment = createSegment(z, isDarkModeRef.current);
      segment.rotation.z = (i * Math.PI) / RING_SECTORS;
      scene.add(segment);
      segments.push(segment);
    }
    segmentsRef.current = segments;

    const findSection = (): HTMLElement | null => {
      let el: HTMLElement | null = containerRef.current;
      while (el) {
        if (el.tagName === 'SECTION') return el;
        el = el.parentElement;
      }
      return null;
    };
    const sectionEl = findSection();

    let scrollRafPending = false;
    const computeScroll = () => {
      scrollRafPending = false;
      let progress = 0;
      if (sectionEl) {
        const rect = sectionEl.getBoundingClientRect();
        const viewportH = window.innerHeight;
        const totalScrollable = sectionEl.offsetHeight - viewportH;
        const scrolled = Math.min(Math.max(-rect.top, 0), Math.max(totalScrollable, 1));
        progress = totalScrollable > 0 ? scrolled / totalScrollable : 0;
      } else {
        progress = Math.min(window.scrollY / 1200, 1);
      }
      scrollProgressRef.current = progress;
      const eased = progress * progress * (3 - 2 * progress);
      targetZRef.current = -eased * MAX_TRAVEL;

      if (hintRef.current) {
        hintRef.current.style.opacity = progress > 0.02 ? '0' : '1';
      }
    };
    const updateScroll = () => {
      if (scrollRafPending) return;
      scrollRafPending = true;
      requestAnimationFrame(computeScroll);
    };
    window.addEventListener('scroll', updateScroll, { passive: true });
    window.addEventListener('resize', updateScroll);
    computeScroll();

    // Pause the render loop while the hero is off-screen or the tab is hidden,
    // so we're not burning CPU on WebGL work nobody sees.
    let isIntersecting = true;
    let isTabVisible = document.visibilityState === 'visible';
    let isActive = true;
    const updateActive = () => {
      isActive = isIntersecting && isTabVisible;
    };
    const intersectionObserver = new IntersectionObserver(
      ([entry]) => {
        isIntersecting = entry.isIntersecting;
        updateActive();
      },
      { threshold: 0 },
    );
    intersectionObserver.observe(containerRef.current);
    const handleVisibilityChange = () => {
      isTabVisible = document.visibilityState === 'visible';
      updateActive();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    let frameId: number;
    const animate = () => {
      frameId = requestAnimationFrame(animate);
      if (!isActive) return;
      const cam = cameraRef.current;
      const scn = sceneRef.current;
      const rndr = rendererRef.current;
      if (!cam || !scn || !rndr) return;

      currentZRef.current += (targetZRef.current - currentZRef.current) * 0.08;
      cam.position.z = currentZRef.current;

      const progress = scrollProgressRef.current;
      cam.rotation.z = progress * Math.PI * 0.6;

      cam.position.x = Math.sin(progress * 6) * 0.15 * progress;
      cam.position.y = Math.cos(progress * 6) * 0.15 * progress;

      const camZ = cam.position.z;
      const dark = isDarkModeRef.current;

      const forwardLimit = SEGMENT_DEPTH * NUM_SEGMENTS;
      const segs = segmentsRef.current;
      let minZ = Infinity;
      let maxZ = -Infinity;
      for (let i = 0; i < segs.length; i++) {
        const z = segs[i].position.z;
        if (z < minZ) minZ = z;
        if (z > maxZ) maxZ = z;
      }

      let recyclesLeft = MAX_RECYCLES_PER_FRAME;
      for (let i = 0; i < segs.length && recyclesLeft > 0; i++) {
        const segment = segs[i];
        const relZ = segment.position.z - camZ;

        if (relZ > SEGMENT_DEPTH) {
          minZ = minZ - SEGMENT_DEPTH;
          segment.position.z = minZ;
          segment.rotation.z += Math.PI / RING_SECTORS;
          recolorPanels(segment, dark);
          recyclesLeft--;
        } else if (relZ < -forwardLimit) {
          maxZ = maxZ + SEGMENT_DEPTH;
          segment.position.z = maxZ;
          segment.rotation.z -= Math.PI / RING_SECTORS;
          recolorPanels(segment, dark);
          recyclesLeft--;
        }
      }

      flickerAccumRef.current += 1;
      if (flickerAccumRef.current >= 8) {
        flickerAccumRef.current = 0;
        segmentsRef.current.forEach((segment) => {
          segment.children.forEach((child) => {
            if (child instanceof THREE.Points && child.name === 'mosaic_panel') {
              const colorAttr = child.geometry.getAttribute('color') as THREE.BufferAttribute;
              const count = colorAttr.count;
              const flickerCount = Math.max(1, Math.floor(count * 0.02));
              for (let k = 0; k < flickerCount; k++) {
                const idx = Math.floor(Math.random() * count);
                const shade = randomShade(dark);
                colorAttr.setXYZ(idx, shade, shade, shade);
              }
              colorAttr.needsUpdate = true;
            }
          });
        });
      }

      rndr.render(scn, cam);
    };
    animate();

    const handleResize = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', updateScroll);
      window.removeEventListener('resize', updateScroll);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      intersectionObserver.disconnect();
      cancelAnimationFrame(frameId);
      renderer.dispose();
    };
  }, []);

  useEffect(() => {
    if (!sceneRef.current) return;

    const bgHex = isDarkMode ? 0x0a0a0a : 0xf2f2f2;
    const fogHex = isDarkMode ? 0x050505 : 0xf2f2f2;
    const lineHex = isDarkMode ? 0x666666 : 0x999999;
    const lineOp = isDarkMode ? 0.45 : 0.55;

    sceneRef.current.background = new THREE.Color(bgHex);
    if (sceneRef.current.fog) {
      (sceneRef.current.fog as THREE.Fog).color.setHex(fogHex);
    }

    segmentsRef.current.forEach((segment) => {
      segment.children.forEach((child) => {
        if (child instanceof THREE.LineSegments) {
          const mat = child.material as THREE.LineBasicMaterial;
          mat.color.setHex(lineHex);
          mat.opacity = lineOp;
          mat.needsUpdate = true;
        } else if (child instanceof THREE.Points && child.name === 'mosaic_panel') {
          const colorAttr = child.geometry.getAttribute('color') as THREE.BufferAttribute;
          const count = colorAttr.count;
          for (let i = 0; i < count; i++) {
            const shade = randomShade(isDarkMode);
            colorAttr.setXYZ(i, shade, shade, shade);
          }
          colorAttr.needsUpdate = true;
        }
      });
    });
  }, [isDarkMode]);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        contentRef.current,
        { opacity: 0, y: 30, scale: 0.95 },
        { opacity: 1, y: 0, scale: 1, duration: 1.2, ease: 'power3.out', delay: 0.4 },
      );
    }, containerRef);
    return () => ctx.revert();
  }, []);

  return (
    <div
      ref={containerRef}
      className={`relative w-full h-screen overflow-hidden transition-colors duration-700 ${
        isDarkMode ? 'bg-[#0a0a0a]' : 'bg-[#f2f2f2]'
      }`}
    >
      <div className="absolute inset-0 w-full h-full overflow-hidden z-0">
        <canvas ref={canvasRef} className="w-full h-full block" />
        <div
          className={`absolute inset-0 pointer-events-none ${
            isDarkMode
              ? 'bg-[radial-gradient(ellipse_at_center,transparent_40%,rgba(0,0,0,0.55)_100%)]'
              : 'bg-[radial-gradient(ellipse_at_center,transparent_40%,rgba(200,200,200,0.5)_100%)]'
          }`}
        />
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.08] mix-blend-overlay"
          style={{
            backgroundImage:
              'repeating-linear-gradient(0deg, rgba(255,255,255,0.7) 0px, rgba(255,255,255,0.7) 1px, transparent 1px, transparent 3px)',
          }}
        />
      </div>

      <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none">
        <div
          ref={contentRef}
          className="text-center flex flex-col items-center max-w-3xl px-6 pointer-events-auto"
        >
          <div
            className={`mb-6 inline-flex items-center gap-2 px-3 py-1 rounded-full border text-[11px] font-mono uppercase tracking-[0.2em] ${
              isDarkMode
                ? 'border-white/20 text-white/70 bg-white/5'
                : 'border-black/15 text-black/60 bg-black/5'
            }`}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />
            Utility Apps & Digital Marketing · est. 2018
          </div>

          <h1
            className={`text-[4.5rem] md:text-[6.5rem] lg:text-[8rem] leading-[0.85] font-bold tracking-tighter mb-8 transition-colors duration-500 ${
              isDarkMode ? 'text-white' : 'text-black'
            }`}
            style={{ textShadow: isDarkMode ? '0 0 40px rgba(0,0,0,0.6)' : '0 0 40px rgba(255,255,255,0.8)' }}
          >
            Apps built <br className="md:hidden" />to perform.
          </h1>

          <p
            className={`text-base md:text-lg font-normal max-w-lg leading-relaxed mb-10 transition-colors duration-500 ${
              isDarkMode ? 'text-white/60' : 'text-black/60'
            }`}
          >
            We build and promote utility applications — from system cleaners and optimisers to{' '}
            <span className={isDarkMode ? 'text-white font-medium' : 'text-black font-medium'}>
              AI-powered services
            </span>
            {' '}— all under one roof.
          </p>

          <div className="flex items-center gap-6">
            <a
              href="/contact"
              className={`rounded-full px-8 py-3.5 text-sm font-medium hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer ${
                isDarkMode
                  ? 'bg-white text-black hover:bg-gray-200'
                  : 'bg-black text-white hover:bg-gray-900'
              }`}
            >
              Get in touch
            </a>
            <a
              href="#portfolio"
              className={`text-sm font-medium hover:opacity-70 transition-opacity flex items-center gap-1 whitespace-nowrap cursor-pointer ${
                isDarkMode ? 'text-white' : 'text-black'
              }`}
            >
              Our products <span>→</span>
            </a>
          </div>
        </div>
      </div>

      <div
        ref={hintRef}
        className={`absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-2 transition-opacity duration-500 pointer-events-none ${
          isDarkMode ? 'text-white/60' : 'text-black/50'
        }`}
      >
        <span className="text-[10px] font-mono uppercase tracking-[0.25em]">Scroll</span>
        <div
          className={`w-[1px] h-10 ${isDarkMode ? 'bg-white/40' : 'bg-black/30'} animate-pulse`}
        />
      </div>
    </div>
  );
};

export default Hero;